import {
  Box,
  Card,
  useMediaQuery,
  useTheme,
  Grid2 as Grid,
  Typography,
  IconButton,
  Chip,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import NotaryIcon from "@mui/icons-material/AccountBalance";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import SubusersGrid from "../datagridActions/SubusersGrid";
import { useFormik } from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";
import { fetchUserData } from "../../store/actions/userActions";
import { useNavigate } from "react-router-dom";
import { fetchPrivateProviderData } from "../../store/actions/privateProviderActions";
import NotaryUserPaymentModal from "../modals/NotaryUserPaymentModal";
import NotaryCancelModal from "../modals/NotaryCancelModal";
import { DateTime } from "luxon";
import {
  getNumericLimit,
  isLimitReached,
  isUnlimitedValue,
} from "../../functions/helperFunctions";
YupPassword(Yup);

const Subusers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCancelExpanded, setIsCancelExpanded] = useState(false);
  const [subuserError, setSubuserError] = useState("");
  const [subuserLoading, setSubuserLoading] = useState(false);
  const [addedText, setAddedText] = useState("");
  const [fetching, setFetching] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [removeSubuserLoading, setRemoveSubuserLoading] = useState(false);
  const [requestsSelectionModel, setRequestsSelectionModel] = useState([]);
  const [isNotaryUser, setIsNotaryUser] = useState(false);
  const [notaryPaymentModalOpen, setNotaryPaymentModalOpen] = useState(false);
  const [notaryUserData, setNotaryUserData] = useState(null);

  // Commission number dialog states for conversion
  const [commissionNumberDialogOpen, setCommissionNumberDialogOpen] =
    useState(false);
  const [commissionNumberInput, setCommissionNumberInput] = useState("");
  const [commissionNumberError, setCommissionNumberError] = useState("");
  const [pendingConversionSubuser, setPendingConversionSubuser] =
    useState(null);

  // Notary cancellation states
  const [notaryCancelModalOpen, setNotaryCancelModalOpen] = useState(false);
  const [notaryCancelLoading, setNotaryCancelLoading] = useState(false);
  const [notaryCancelResponse, setNotaryCancelResponse] = useState("");
  const [notaryCancelError, setNotaryCancelError] = useState(false);
  const [cancelActionSubuserId, setCancelActionSubuserId] = useState(null);
  const [deleteSubuserLoading, setDeleteSubuserLoading] = useState(false);

  //? SCREEN SIZES ##################################################################
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largePC = useMediaQuery(theme.breakpoints.up("xl"));

  //? ###############################################################################

  const fetchUserOnceRef = useRef();

  useEffect(() => {
    if (currentUser?.user?.expired === true) {
      navigate("/subscription");
    }
  }, [currentUser?.user?.expired, navigate]);

  // Update selectedUser when currentUser changes (e.g., from WebSocket updates)
  useEffect(() => {
    if (selectedUser && currentUser?.subusers_row) {
      const updatedSubuser = currentUser.subusers_row.find(
        (sub) => sub.id === selectedUser.id || sub.email === selectedUser.email,
      );
      if (updatedSubuser) {
        setSelectedUser(updatedSubuser);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.subusers_row, selectedUser?.id, selectedUser?.email]);

  const addSubuserFormik = useFormik({
    enableReinitialize: false,
    initialValues: {
      first: "",
      last: "",
      email: "",
      role: "",
      commission_number: "",
    },
    validationSchema: Yup.object({
      first: Yup.string().min(3).required("First name is required"),
      last: Yup.string().min(3).required("Last name is required"),
      email: Yup.string()
        .required("Email is required")
        .email("Must be a valid email"),
      role: Yup.string().required("Role is required"),
      commission_number: Yup.string().when([], {
        is: () => isNotaryUser,
        then: (schema) =>
          schema.required("Commission number is required for notary users"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, helpers) => {
      setSubuserLoading(true);
      values["timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Check if this is a notary user
      if (isNotaryUser) {
        // Store the form data and open payment modal
        const payload = {
          ...values,
          addNotary: true,
          commission_number: values.commission_number,
        };
        setNotaryUserData(payload);
        setNotaryPaymentModalOpen(true);
        setSubuserLoading(false);
        return;
      }

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/subuser/add-subuser`,
          {
            values,
            currentSession: currentSession,
          },
          { withCredentials: true },
        );
        setIsExpanded(false);
        setAddedText(resp.data.message);
        helpers.resetForm();
        setIsNotaryUser(false);

        // Clear selection and refresh data after successful addition
        setRequestsSelectionModel([]);
        setSelectedUser(null);
        handleFetch(currentSession);
      } catch (e) {
        setSubuserError(e.response.data.message);

        if (e.response.data?.type === "email") {
          helpers.setFieldValue("email", "");
        }
      }

      setSubuserLoading(false);
    },
  });

  useEffect(() => {
    if (subuserError !== "") {
      const subuserTimer = setInterval(() => {
        setSubuserError("");
      }, 5000);
      return () => clearInterval(subuserTimer);
    }

    if (addedText !== "") {
      const addedTextTimer = setInterval(() => {
        setAddedText("");
      }, 5000);
      return () => clearInterval(addedTextTimer);
    }
  }, [subuserError, addedText]);

  const handleFetch = async (currentSession) => {
    setFetching(true);
    try {
      if (currentSession === "privateProviderSession") {
        await dispatch(fetchPrivateProviderData(currentSession));
      } else {
        await dispatch(fetchUserData(currentSession));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const fetchUserOnce = async (currentSession) => {
      if (currentSession === "privateProviderSession") {
        dispatch(fetchPrivateProviderData(currentSession));
      } else {
        dispatch(fetchUserData(currentSession));
      }
    };

    if (!fetchUserOnceRef?.current) {
      fetchUserOnceRef.current = true;
      fetchUserOnce(currentSession);
    }
  }, [currentSession, dispatch]);

  const handleNotaryPaymentSuccess = (message) => {
    setAddedText(message);
    setIsExpanded(false);
    addSubuserFormik.resetForm();
    setIsNotaryUser(false);
    setNotaryUserData(null);

    // Clear selection and refresh data after successful addition
    setRequestsSelectionModel([]);
    setSelectedUser(null);
    // Refresh user data
    handleFetch(currentSession);
  };

  const handleNotaryPaymentError = (message) => {
    setSubuserError(message);
    setNotaryPaymentModalOpen(false);
  };

  const getNotaryUserLimit = () => {
    // Admin users get 10 notary users
    if (currentUser?.user?.role === "admin") {
      return 10;
    }

    const limit = currentUser?.service_tiers?.notary_user?.limit || 0;

    if (typeof limit === "string" && limit?.toLowerCase() === "unlimited") {
      return 99999999;
    }

    return Number(currentUser?.service_tiers?.notary_user?.limit || 0);
  };

  const getCurrentNotaryUserCount = () => {
    let subUsersNotaryCount = 0;

    if (!currentUser?.subusers_row) {
      subUsersNotaryCount = 0;
    } else {
      subUsersNotaryCount = currentUser.subusers_row.filter(
        (subuser) => subuser.notary_user?.is_notary,
      ).length;
    }

    const mainUserNotaryCount = currentUser?.user?.notary_user?.is_notary
      ? 1
      : 0;

    return subUsersNotaryCount + mainUserNotaryCount;
  };

  const canAddNotaryUser = () => {
    if (currentUser?.tier?.title === "Free") return false;

    const limit = getNotaryUserLimit();
    const currentCount = getCurrentNotaryUserCount();

    if (limit === "Unlimited") return true;
    return currentCount < limit;
  };

  const subuserLimit = currentUser?.tier?.subuser;
  const hasUnlimitedSubuserLimit = isUnlimitedValue(subuserLimit);
  const numericSubuserLimit = getNumericLimit(subuserLimit);

  const handleNotaryConversion = async (subuser) => {
    try {
      // Store the subuser and open commission number dialog first
      setPendingConversionSubuser(subuser);
      setCommissionNumberInput("");
      setCommissionNumberError("");
      setCommissionNumberDialogOpen(true);
    } catch (error) {
      console.error("Error opening notary conversion:", error);
      setSubuserError("Failed to open notary conversion. Please try again.");
    }
  };

  const handleCommissionNumberSubmit = () => {
    // Validate commission number
    if (!commissionNumberInput.trim()) {
      setCommissionNumberError("Commission number is required");
      return;
    }

    // Close dialog and open payment modal with commission number
    setCommissionNumberDialogOpen(false);

    if (pendingConversionSubuser) {
      setNotaryUserData({
        first:
          pendingConversionSubuser.first ||
          pendingConversionSubuser.name?.split(" ")[0],
        last:
          pendingConversionSubuser.last ||
          pendingConversionSubuser.name?.split(" ").slice(1).join(" "),
        email: pendingConversionSubuser.email,
        role: pendingConversionSubuser.role,
        isConversion: true,
        subuserId:
          pendingConversionSubuser.fullId || pendingConversionSubuser.id,
        commission_number: commissionNumberInput.trim(),
      });
      setNotaryPaymentModalOpen(true);
      setPendingConversionSubuser(null);
    }
  };

  const handleCommissionNumberDialogClose = () => {
    setCommissionNumberDialogOpen(false);
    setCommissionNumberInput("");
    setCommissionNumberError("");
    setPendingConversionSubuser(null);
  };

  // Notary cancellation handlers for subusers
  const handleSubuserNotaryCancelClick = async (subuser) => {
    const isCancelling = subuser?.notary_user?.cancelling?.started_at;
    setCancelActionSubuserId(subuser.id);

    if (isCancelling) {
      // Resume subscription
      try {
        setNotaryCancelLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/resume-notary-subscription`,
          {
            email: subuser.email,
            currentSession: currentSession,
          },
          { withCredentials: true },
        );

        if (resp?.data?.success) {
          setNotaryCancelResponse(resp?.data?.message);
          setNotaryCancelError(false);
          // Refresh user data to update UI
          if (currentSession === "privateProviderSession") {
            dispatch(fetchPrivateProviderData(currentSession));
          } else {
            dispatch(fetchUserData(currentSession));
          }
        } else {
          setNotaryCancelError(true);
          setNotaryCancelResponse(resp?.data?.message);
        }
      } catch (e) {
        setNotaryCancelError(true);
        setNotaryCancelResponse(
          "An error occurred while resuming the subscription. Please try again.",
        );
      } finally {
        setNotaryCancelLoading(false);
        setTimeout(() => {
          setNotaryCancelError(false);
          setNotaryCancelResponse("");
          setCancelActionSubuserId(null);
        }, 3500);
      }
    } else {
      // Open cancellation modal
      setSelectedUser(subuser);
      setNotaryCancelModalOpen(true);
    }
  };

  const handleSubuserNotaryCancelConfirm = async () => {
    try {
      setNotaryCancelLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/delete-notary-user`,
        {
          email: selectedUser.email,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setNotaryCancelResponse(resp?.data?.message);
        setNotaryCancelError(false);
        setNotaryCancelModalOpen(false);
        // Refresh user data immediately to update the UI
        if (currentSession === "privateProviderSession") {
          dispatch(fetchPrivateProviderData(currentSession));
        } else {
          dispatch(fetchUserData(currentSession));
        }
        setTimeout(() => {
          setNotaryCancelLoading(false);
        }, 1000);
      } else {
        setNotaryCancelError(true);
        setNotaryCancelResponse(resp?.data?.message);
        setNotaryCancelLoading(false);
      }
    } catch (e) {
      setNotaryCancelError(true);
      setNotaryCancelResponse(
        "An error occurred while canceling the notary. Please try again.",
      );
      setNotaryCancelLoading(false);
    } finally {
      setTimeout(() => {
        setNotaryCancelError(false);
        setNotaryCancelResponse("");
      }, 3500);
    }
  };

  const handleSubuserNotaryCancelClose = () => {
    setNotaryCancelModalOpen(false);
  };

  const handleDeleteSubuser = async (subuser) => {
    if (!subuser) return;

    // Double check if notary user
    if (subuser.notary_user?.is_notary) {
      setSubuserError(
        "Cannot delete a notary subuser. Please cancel their notary subscription first.",
      );
      setTimeout(() => setSubuserError(""), 3500);
      return;
    }

    try {
      setDeleteSubuserLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/subusers/delete-subuser`,
        {
          email: subuser.email,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        setAddedText(resp?.data?.message || "Subuser deleted successfully");
        setSelectedUser(null);
        // Refresh user data
        if (currentSession === "privateProviderSession") {
          dispatch(fetchPrivateProviderData(currentSession));
        } else {
          dispatch(fetchUserData(currentSession));
        }
      } else {
        setSubuserError(resp?.data?.message || "Failed to delete subuser");
      }
    } catch (e) {
      setSubuserError("An error occurred while deleting the subuser");
    } finally {
      setDeleteSubuserLoading(false);
      setTimeout(() => {
        setAddedText("");
        setSubuserError("");
      }, 3500);
    }
  };

  return (
    <>
      <Box
        sx={{
          mx: largePC ? "auto" : 0,
          maxWidth: "1500px",
          py: 1,
          mt: 2,
          backgroundColor: "#EEF2F6",
          mb: tabletSize ? "5rem" : undefined,
        }}
      >
        <Grid
          container
          sx={{
            px: mobileSize ? 1 : tabletSize ? 2 : 3,
          }}
        >
          <Card
            py={tabletSize ? 0 : 3}
            px={tabletSize ? 0 : 3}
            component={Grid}
            container
          >
            <Grid container>
              {!tabletSize && (
                <Grid size={{ xs: 12 }} sx={{ mb: 3, position: "relative" }}>
                  <Typography variant="h5" align="center" color="primary">
                    Users {currentUser?.subusers.length}/
                    {hasUnlimitedSubuserLimit
                      ? "Unlimited"
                      : numericSubuserLimit}
                  </Typography>
                  {!hasUnlimitedSubuserLimit && numericSubuserLimit === 0 ? (
                    <></>
                  ) : (
                    <IconButton
                      component="span"
                      sx={{ position: "absolute", right: 0, top: -5 }}
                      className={fetching ? "rotate" : ""}
                      color="primary"
                      onClick={() => handleFetch(currentSession)}
                      disabled={currentUser?.subusers_row?.length === 0}
                    >
                      <RefreshIcon />
                    </IconButton>
                  )}
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <SubusersGrid
                  setRemoveSubuserLoading={setRemoveSubuserLoading}
                  removeSubuserLoading={removeSubuserLoading}
                  currentUser={currentUser}
                  subusersRow={currentUser?.subusers_row || []}
                  formik={addSubuserFormik}
                  setIsCancelExpanded={setIsCancelExpanded}
                  isCancelExpanded={isCancelExpanded}
                  isExpanded={isExpanded}
                  setIsExpanded={setIsExpanded}
                  subuserError={subuserError}
                  setSubuserError={setSubuserError}
                  subuserLoading={subuserLoading}
                  addedText={addedText}
                  setSelectedUser={setSelectedUser}
                  selectedUser={selectedUser}
                  requestsSelectionModel={requestsSelectionModel}
                  setRequestsSelectionModel={setRequestsSelectionModel}
                  mobileSize={mobileSize}
                  tabletSize={tabletSize}
                  handleFetch={handleFetch}
                  currentSession={currentSession}
                  fetching={fetching}
                />
              </Grid>
            </Grid>
          </Card>
          {/* SUBUSER DETAILS */}
          {selectedUser && (
            <Grid container spacing={2} sx={{ mt: 2, width: "100%" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Card sx={{ height: "100%", p: 3 }}>
                  <Typography variant="h5" gutterBottom color="primary">
                    {selectedUser.name} - User Details
                  </Typography>

                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Account Information
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            wordBreak: "break-all",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          <strong>Email:</strong> {selectedUser.email}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Role:</strong> {selectedUser.role}
                        </Typography>
                        <Box
                          sx={{ mb: 1, display: "flex", alignItems: "center" }}
                        >
                          <Typography variant="body2" component="span">
                            <strong>Status:</strong>
                          </Typography>
                          <Chip
                            label={selectedUser.status}
                            color={
                              selectedUser.status === "active"
                                ? "success"
                                : "warning"
                            }
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Created:</strong>{" "}
                          {new Date(
                            selectedUser.creationDate,
                          ).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Last Login:</strong>{" "}
                          {selectedUser.lastLogin
                            ? new Date(
                                selectedUser.lastLogin,
                              ).toLocaleDateString()
                            : "Never"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Notary Status
                        </Typography>
                        {selectedUser.notary_user?.is_notary ? (
                          <Box>
                            <Chip
                              icon={<NotaryIcon />}
                              label="Notary User"
                              color={
                                selectedUser?.notary_user?.cancelling
                                  ?.started_at
                                  ? "warning"
                                  : "primary"
                              }
                              variant="filled"
                              sx={{ mb: 2 }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              This user has notary capabilities and can perform
                              notarizations.
                            </Typography>

                            {selectedUser?.notary_user?.cancelling
                              ?.started_at && (
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color="warning.main"
                                sx={{ mb: 2 }}
                              >
                                {(() => {
                                  const hasPendingTasks =
                                    selectedUser.notary_user.cancelling
                                      .pending_task_ids?.length > 0;

                                  if (hasPendingTasks) {
                                    return "Cancellation pending - will complete after pending tasks";
                                  } else {
                                    const cancellationDate = DateTime.fromISO(
                                      selectedUser.notary_user.cancelling
                                        .started_at,
                                    ).plus({ hours: 24 });
                                    return `Scheduled for cancellation: ${cancellationDate.toFormat("MMM dd, yyyy hh:mm a")}`;
                                  }
                                })()}
                              </Typography>
                            )}

                            {notaryCancelResponse &&
                              cancelActionSubuserId === selectedUser.id && (
                                <Alert
                                  severity={
                                    notaryCancelError ? "error" : "success"
                                  }
                                  sx={{ mb: 2 }}
                                >
                                  {notaryCancelResponse}
                                </Alert>
                              )}

                            <LoadingButton
                              loading={notaryCancelLoading}
                              variant="contained"
                              color={
                                selectedUser?.notary_user?.cancelling
                                  ?.started_at
                                  ? "primary"
                                  : "error"
                              }
                              size="small"
                              fullWidth
                              onClick={() =>
                                handleSubuserNotaryCancelClick(selectedUser)
                              }
                            >
                              {selectedUser?.notary_user?.cancelling?.started_at
                                ? "Resume Notary Subscription"
                                : "Cancel Notary Subscription"}
                            </LoadingButton>
                          </Box>
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Regular subuser - no notary capabilities
                            </Typography>
                            {selectedUser.status === "active" &&
                              canAddNotaryUser() && (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<NotaryIcon />}
                                  size="small"
                                  onClick={() =>
                                    handleNotaryConversion(selectedUser)
                                  }
                                >
                                  Convert to Notary
                                </Button>
                              )}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: "100%", p: 3 }}>
                  <Typography variant="h6" gutterBottom color="text.secondary">
                    Activity Summary
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">NOCs Created:</Typography>
                      <Typography variant="h6" color="primary">
                        {selectedUser?.nocCreated || 0}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">Permit Packages:</Typography>
                      <Typography variant="h6" color="primary">
                        {selectedUser?.ppCreated || 0}
                      </Typography>
                    </Box>

                    {selectedUser?.nocUsage &&
                      selectedUser.nocUsage.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Usage This Year:
                          </Typography>
                          {selectedUser.nocUsage
                            .filter(
                              (item) =>
                                item.year ===
                                new Date().getFullYear().toString(),
                            )
                            .map((item, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Typography variant="caption">
                                  {new Date(0, item.month).toLocaleString(
                                    "default",
                                    { month: "short" },
                                  )}
                                  :
                                </Typography>
                                <Typography variant="caption" color="primary">
                                  {item.noc} NOCs
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      )}
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", justifyContent: "center" }}
                  >
                    <LoadingButton
                      loading={deleteSubuserLoading}
                      variant="contained"
                      color="error"
                      disabled={selectedUser?.notary_user?.is_notary}
                      onClick={() => handleDeleteSubuser(selectedUser)}
                    >
                      Delete Subuser
                    </LoadingButton>
                  </Box>
                  {selectedUser?.notary_user?.is_notary && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: "center", display: "block", mt: 1 }}
                    >
                      Cancel Notary Subscription First
                    </Typography>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Commission Number Dialog for Conversion */}
      <Dialog
        open={commissionNumberDialogOpen}
        onClose={handleCommissionNumberDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter Commission Number</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To convert {pendingConversionSubuser?.name} to a Notary User, please
            enter their commission number.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Commission Number"
            type="text"
            fullWidth
            variant="filled"
            value={commissionNumberInput}
            onChange={(e) => {
              setCommissionNumberInput(e.target.value);
              setCommissionNumberError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCommissionNumberSubmit();
              }
            }}
            error={Boolean(commissionNumberError)}
            helperText={commissionNumberError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommissionNumberDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCommissionNumberSubmit}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notary User Payment Modal */}
      <NotaryUserPaymentModal
        open={notaryPaymentModalOpen}
        onClose={() => setNotaryPaymentModalOpen(false)}
        notaryUserData={notaryUserData}
        currentUser={currentUser}
        currentSession={currentSession}
        onPaymentSuccess={handleNotaryPaymentSuccess}
        onPaymentError={handleNotaryPaymentError}
        fromSubusersPage={true}
      />

      {/* Notary Cancellation Modal */}
      <NotaryCancelModal
        handleNotaryCancelConfirm={handleSubuserNotaryCancelConfirm}
        notaryCancelModalOpen={notaryCancelModalOpen}
        handleNotaryCancelClose={handleSubuserNotaryCancelClose}
        notaryCancelLoading={notaryCancelLoading}
        responseMessage={notaryCancelResponse}
        isError={notaryCancelError}
      />
    </>
  );
};

export default Subusers;
